import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowRight, Bookmark, RefreshCw } from "lucide-react";
import { BASE_URL, questionsAPI, fullmocktestApi } from "../../../api/api";
import "./TestPanel.css";

/**
 * Notes:
 * - fullmocktestApi returns an ARRAY of quizzes. We pick the first quiz that:
 *    - has test_question as non-null array
 *    - and is status true
 * - questionsAPI returns an ARRAY of question objects (option_a..option_d)
 * - options are built from option_a..option_d (some may be null)
 * - question_text and option values may contain HTML -> rendered with dangerouslySetInnerHTML
 */

const ObjectiveTestPanel = () => {
  const [questions, setQuestions] = useState([]); // filtered questions for chosen quiz
  const [quiz, setQuiz] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { questionId: optionValue }
  const [flagged, setFlagged] = useState({}); // { questionId: true }
  const [loading, setLoading] = useState(true);

  // choose quiz: pick first active quiz that has test_question array
  const chooseQuizFromList = (quizList = []) => {
    if (!Array.isArray(quizList)) return null;
    return quizList.find((q) => Array.isArray(q.test_question) && q.test_question.length);
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        // fetch quizzes (array) and questions (array)
        const [quizRes, questionRes] = await Promise.all([
          axios.get(`${BASE_URL}${fullmocktestApi}`),
          axios.get(`${BASE_URL}${questionsAPI}`),
        ]);

        const quizData = Array.isArray(quizRes?.data)
          ? chooseQuizFromList(quizRes.data)
          : chooseQuizFromList(quizRes.data?.data ?? quizRes.data);

        if (!quizData) {
          toast.warn("No active objective quiz with questions found.");
          setQuiz(null);
          setQuestions([]);
          setLoading(false);
          return;
        }

        // questionRes.data expected as array
        const allQuestions = Array.isArray(questionRes?.data) ? questionRes.data : questionRes.data?.data ?? [];

        // test_question contains IDs like [11, 8]. Match those IDs in question list
        const filtered = quizData.test_question
          .map((id) => allQuestions.find((q) => Number(q.id) === Number(id)))
          .filter(Boolean);

        if (!filtered.length) {
          toast.warn("No matching questions found for the selected quiz.");
        }

        setQuiz(quizData);
        setQuestions(filtered);
        setCurrentIndex(0);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load quiz/questions. Check console for details.");
        setQuiz(null);
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // build options array for current question
  const getOptionsFor = (q) => {
    if (!q) return [];
    // prefer option_a..option_d fields. Keep order A,B,C,D
    const raw = [q.option_a, q.option_b, q.option_c, q.option_d];
    // filter out null/empty
    return raw
      .map((opt) => (opt === null || opt === undefined ? null : String(opt)))
      .filter(Boolean);
  };

  const currentQuestion = useMemo(() => questions[currentIndex], [questions, currentIndex]);

  const handleOptionChange = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const handleClear = () => {
    const qid = currentQuestion?.id;
    if (!qid) return;
    setAnswers((prev) => {
      const updated = { ...prev };
      delete updated[qid];
      return updated;
    });
    toast.info("Response cleared");
  };

  const handleMarkAndNext = () => {
    const qid = currentQuestion?.id;
    if (qid) {
      setFlagged((prev) => ({ ...prev, [qid]: true }));
    }
    toast.success("Marked and moving to next");
    moveNext();
  };

  const handleSaveAndNext = () => {
    const qid = currentQuestion?.id;
    if (qid && answers[qid]) {
      toast.success("Saved answer and moving to next");
    } else {
      toast.info("No answer selected — moving to next");
    }
    moveNext();
  };

  const moveNext = () => {
    setCurrentIndex((prev) => {
      if (prev < questions.length - 1) return prev + 1;
      toast.info("You have reached the last question");
      return prev;
    });
  };

  const movePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  // click question index
  const goToQuestion = (i) => {
    if (i < 0 || i >= questions.length) return;
    setCurrentIndex(i);
  };

  // small helper to render HTML content safely
  const RenderHTML = ({ html }) => (
    <span dangerouslySetInnerHTML={{ __html: html ?? "" }} />
  );

  return (
    <div className="mock-test-panel-container">

      <header className="mock-test-panel-header">
        <div className="mock-test-panel-header-inner">
          <div className="mock-test-panel-title">
            <h1>{quiz?.test_name ?? "Objective Test"}</h1>
            <p className="mock-test-panel-subtitle">{quiz?.test_description}</p>
          </div>

          <div className="mock-test-panel-meta">
            <div className="mock-test-panel-progress">
              <div className="mock-test-panel-progress-bar">
                <div
                  className="mock-test-panel-progress-fill"
                  style={{
                    width: questions.length ? `${((Object.keys(answers).length) / questions.length) * 100}%` : "0%",
                  }}
                />
              </div>
              <small>
                {questions.length ? `${Object.keys(answers).length}/${questions.length} answered` : "0/0"}
              </small>
            </div>
          </div>
        </div>
      </header>

      <main className="mock-test-panel-body">
        {/* Left: question index (25%) */}
        <aside className="mock-test-panel-sidebar" aria-label="Question list">
          <h3 className="mock-test-panel-sidebar-title">Questions</h3>

          <div className="mock-test-panel-question-list">
            {loading ? (
              <div className="mock-test-panel-loading">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="mock-test-panel-empty">No questions available</div>
            ) : (
              questions.map((q, i) => {
                const answered = !!answers[q.id];
                const isFlagged = !!flagged[q.id];
                return (
                  <button
                    key={q.id}
                    onClick={() => goToQuestion(i)}
                    className={`mock-test-panel-qnav ${i === currentIndex ? "active" : ""} ${answered ? "answered" : ""} ${isFlagged ? "flagged" : ""}`}
                    aria-label={`Question ${i + 1}${answered ? " answered" : ""}${isFlagged ? " flagged" : ""}`}
                    title={`Question ${i + 1}`}
                  >
                    <div className="mock-test-panel-qnum">{i + 1}</div>
                    <div className="mock-test-panel-qbadge">
                      {isFlagged ? <Bookmark size={12} /> : answered ? "✓" : ""}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="mock-test-panel-sidebar-actions">
            <button
              className="mock-test-panel-sidebar-prev"
              onClick={movePrev}
              disabled={currentIndex === 0}
            >
              Prev
            </button>
            <button
              className="mock-test-panel-sidebar-next"
              onClick={moveNext}
              disabled={currentIndex >= questions.length - 1}
            >
              Next
            </button>
          </div>
        </aside>

        {/* Right: question + options (75%) */}
        <section className="mock-test-panel-question-area">
          {loading ? (
            <div className="mock-test-panel-loading-large">Loading...</div>
          ) : !currentQuestion ? (
            <div className="mock-test-panel-empty-large">No question to display</div>
          ) : (
            <article className="mock-test-panel-question-card">
              <header className="mock-test-panel-question-head">
                <div className="mock-test-panel-qmeta">
                  <span className="mock-test-panel-subject-name">{currentQuestion.subject_name}</span>
                  <span className="mock-test-panel-topic-name">{currentQuestion.topic_name}</span>
                </div>
                <div className="mock-test-panel-qindex">Q {currentIndex + 1} / {questions.length}</div>
              </header>

              <div className="mock-test-panel-qtext">
                <RenderHTML html={currentQuestion.question_text} />
              </div>

              <div className="mock-test-panel-options">
                {getOptionsFor(currentQuestion).map((opt, idx) => {
                  const qid = currentQuestion.id;
                  const checked = answers[qid] === opt;
                  return (
                    <label key={idx} className="mock-test-panel-option">
                      <input
                        type="radio"
                        name={`q-${qid}`}
                        value={opt}
                        checked={checked}
                        onChange={() => handleOptionChange(qid, opt)}
                      />
                      <div className="mock-test-panel-option-content">
                        <span className="mock-test-panel-option-label">{String.fromCharCode(65 + idx)}.</span>
                        <span className="mock-test-panel-option-text"><RenderHTML html={opt} /></span>
                      </div>
                    </label>
                  );
                })}
              </div>

              <footer className="mock-test-panel-actions">
                <button className="mock-test-panel-btn clear" onClick={handleClear}>
                  <RefreshCw size={16} /> Clear Response
                </button>

                <button className="mock-test-panel-btn mark" onClick={handleMarkAndNext}>
                  <Bookmark size={16} /> Mark & Next
                </button>

                <button className="mock-test-panel-btn save" onClick={handleSaveAndNext}>
                  Save & Next <ArrowRight size={16} />
                </button>
              </footer>
            </article>
          )}
        </section>
      </main>
    </div>
  );
};

export default ObjectiveTestPanel;
